/**
 * Pure helpers for the AI Upload destination picker.
 *
 * The picker asks the user "Upload to: <folder>" before AI Upload sends files
 * to the backend. Default selection rules (highest precedence first):
 *
 *   1. The matter's "AI Claim File" folder if one exists at the matter root.
 *      That folder exists specifically for AI-processed claim documents and is
 *      the entire reason this picker defaults something.
 *   2. The folder the user is currently viewing in the tree (if any).
 *   3. Matter root (no folder; backend places into its default location).
 *
 * Comparison is case-insensitive and tolerates trailing whitespace, but does
 * NOT match other "AI"-prefixed folders (a folder named "AI Notes" or
 * "AI Claim File 2" should not silently win the default).
 */

const AI_CLAIM_FILE_LABEL = "ai claim file";

const normalizeTitle = (title) =>
  typeof title === "string" ? title.trim().toLowerCase() : "";

const folderDestinationKey = (folder) => {
  const explicitKey = folder?.key || folder?.unique_id;
  if (explicitKey) return String(explicitKey);
  return folder?.id ? `F${folder.id}` : null;
};

const folderDestinationTitle = (folder) => {
  if (typeof folder?.title === "string") return folder.title;
  if (typeof folder?.name === "string") return folder.name;
  return "";
};

const isFolderRow = (row) =>
  row?.type === "folder" ||
  row?.item_type === "folder" ||
  row?.kind === "folder" ||
  (
    !row?.file_type &&
    !row?.filename &&
    (Array.isArray(row?.subfolders) || Array.isArray(row?.documents))
  );

export const folderRowsToDestinationNodes = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row) => row && typeof row === "object" && isFolderRow(row))
    .map((row) => ({
      key: folderDestinationKey(row),
      title: folderDestinationTitle(row),
      type: "folder",
      children: [],
    }))
    .filter((row) => row.key);
};

export const mergeFolderDestinationTree = (treeData, rows) => {
  const baseTree = Array.isArray(treeData) ? treeData : [];
  const existingKeys = new Set(flattenFolderTree(baseTree).map((folder) => folder.key));
  const visibleRows = folderRowsToDestinationNodes(rows).filter(
    (folder) => !existingKeys.has(String(folder.key)),
  );
  return visibleRows.length > 0 ? [...baseTree, ...visibleRows] : baseTree;
};

/**
 * Walk the matter tree and return all folder nodes flattened into a list with
 * indentation depth. Documents are skipped. Order follows tree DFS so the
 * dropdown reads top-down the way a human sees the sidebar tree.
 *
 * Each result entry: { key, title, depth }.
 */
export const flattenFolderTree = (treeData, depth = 0) => {
  if (!Array.isArray(treeData)) return [];
  const out = [];
  for (const node of treeData) {
    if (!node || typeof node !== "object") continue;
    if (node.type === "folder" && node.key) {
      out.push({
        key: String(node.key),
        title: typeof node.title === "string" ? node.title : "",
        depth,
      });
      if (Array.isArray(node.children) && node.children.length > 0) {
        out.push(...flattenFolderTree(node.children, depth + 1));
      }
    }
  }
  return out;
};

/**
 * Returns the key of the matter's "AI Claim File" folder, or null if none.
 * Only matches folders whose normalized title is exactly "ai claim file" so
 * "AI Notes" / "AI Claim File 2" don't get accidentally promoted.
 *
 * Searches the entire tree (not just the root level) because in some matter
 * shapes the AI Claim File folder can be nested under a parent folder rather
 * than at the matter root.
 */
export const findAIClaimFileFolder = (treeData) => {
  const folders = flattenFolderTree(treeData);
  const hit = folders.find((f) => normalizeTitle(f.title) === AI_CLAIM_FILE_LABEL);
  return hit ? hit.key : null;
};

/**
 * Resolve the default destination for an AI Upload. Returns either a folder
 * key (string) or null (= matter root).
 *
 * Precedence: AI Claim File > current folder the user is viewing > matter root.
 *
 * `currentFolderKey` is whatever `getSelectedKey()` returned in UploadButton —
 * either an `Fxxxxx` folder key, a `Dxxxxx` document key, or null. Document
 * keys are not valid destinations and fall through to the next precedence
 * (matter root).
 */
export const getDefaultDestinationKey = (treeData, currentFolderKey) => {
  const aiClaimFile = findAIClaimFileFolder(treeData);
  if (aiClaimFile) return aiClaimFile;
  if (typeof currentFolderKey === "string" && currentFolderKey.startsWith("F")) {
    return currentFolderKey;
  }
  return null;
};

/**
 * Visible-label helper for the picker dropdown. Indents nested folders with a
 * leading spacer. Kept here (instead of inline in the modal) so it's testable.
 */
export const formatFolderOptionLabel = ({ title, depth }) => {
  const safeTitle = title || "(untitled folder)";
  if (!depth || depth < 1) return safeTitle;
  // Two non-breaking spaces per level — visible in the dropdown without
  // collapsing in HTML normalization.
  return `${"\u00A0\u00A0".repeat(depth)}${safeTitle}`;
};
